<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { useAuthStore } from '@/store/AuthStore'
import { authApi } from '@/api'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useGoogleAuth } from '@/composables/useGoogleAuth'
import { useAnalytics } from '@/composables/useAnalytics'
import { getErrorStatus } from '@/utils/error'
import { getUtmParams, getSourceString } from '@/utils/utm'

const { capture } = useAnalytics()

type Mode = 'choose' | 'email'
const mode = ref<Mode>('choose')

const displayName = ref('')
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')

const router = useRouter()
const auth = useAuthStore()

const googleAuth = useGoogleAuth()
const isGoogleLoading = ref(false)

const marketingUrl = import.meta.env.VITE_MARKETING_URL || 'https://smart-teacher.net'

onMounted(() => {
  capture('signup_screen_viewed')
  googleAuth.initialize(handleGoogleCredential)
  googleAuth.registerButtonContainer('google-signup-button')
})

async function handleGoogleCredential(idToken: string) {
  isGoogleLoading.value = true
  errorMessage.value = ''
  try {
    const source = getSourceString()
    await authApi.authGooglePost({ idToken, ...(source ? { source } : {}) } as any)
    await auth.onAuthenticated()
    capture('signed_up', { method: 'google', ...getUtmParams() })
    router.push('/')
  } catch (e: unknown) {
    console.error('Google sign up failed:', e)
    errorMessage.value =
      getErrorStatus(e) === 401
        ? 'Google authentication failed. Please try again.'
        : 'Unable to sign up with Google. Please try again.'
  } finally {
    isGoogleLoading.value = false
  }
}

const isFormValid = computed(() => email.value.includes('@') && password.value.length >= 8)

const passwordStrength = computed(() => {
  const pw = password.value
  if (!pw) return null
  let score = 0
  if (pw.length >= 8) score++
  if (pw.length >= 12) score++
  if (/\d/.test(pw)) score++
  if (/[A-Z]/.test(pw) || /[^A-Za-z0-9]/.test(pw)) score++
  const filled = Math.min(Math.max(score, 1), 4)
  if (filled <= 1) return { filled: 1, word: 'Weak', bar: 'bg-danger-ink', text: 'text-danger-ink' }
  if (filled === 2) return { filled: 2, word: 'Fair', bar: 'bg-warning-ink', text: 'text-warning-ink' }
  return { filled, word: 'Strong', bar: 'bg-success-ink', text: 'text-success-ink' }
})

async function handleRegister() {
  if (!isFormValid.value) return
  errorMessage.value = ''
  isLoading.value = true
  try {
    const source = getSourceString()
    await authApi.authRegisterPost({
      email: email.value,
      password: password.value,
      ...(displayName.value.trim() ? { displayName: displayName.value.trim() } : {}),
      ...(source ? { source } : {}),
    } as any)
    await auth.onAuthenticated()
    capture('signed_up', { method: 'email', ...getUtmParams() })
    router.push('/')
  } catch (e: unknown) {
    console.error('Registration failed:', e)
    const status = getErrorStatus(e)
    if (status === 409) errorMessage.value = 'An account with this email already exists. Try signing in instead.'
    else if (status === 400) errorMessage.value = 'Please check your information and try again.'
    else errorMessage.value = 'Unable to create account. Please check your connection and try again.'
  } finally {
    isLoading.value = false
  }
}

function startEmail() {
  capture('signup_method_selected', { method: 'email' })
  mode.value = 'email'
}
</script>

<template>
  <div class="w-full max-w-md">
    <!-- CHOOSE: brand hero + auth options (mirrors mobile Create Account) -->
    <template v-if="mode === 'choose'">
      <div class="relative overflow-hidden rounded-3xl bg-surface-variant px-6 py-7 mb-6">
        <div class="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-coral/10 pointer-events-none"></div>
        <div class="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-ink-black/[0.06] pointer-events-none"></div>
        <div class="relative text-center">
          <h1 class="text-[28px] leading-8 font-bold tracking-tight text-ink-900">Less admin.<br />More teaching.</h1>
          <p class="mt-2 mx-auto max-w-[260px] text-[15px] leading-snug text-ink-600">
            With AI quietly handling the busywork.
          </p>
          <div class="mt-5 flex items-center justify-center gap-2">
            <span
              v-for="p in ['Manage', 'Plan', 'Connect']"
              :key="p"
              class="inline-flex items-center gap-1.5 rounded-full bg-surface border border-ink-200 px-2.5 py-1.5"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-[var(--primary)]"></span>
              <span class="text-[13px] font-semibold text-ink-800">{{ p }}</span>
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="errorMessage"
        class="mb-4 p-4 bg-danger-soft border border-danger-ink/15 rounded-xl flex items-start gap-3"
      >
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="text-danger-ink mt-0.5" />
        <p class="text-sm text-danger-ink">{{ errorMessage }}</p>
      </div>

      <div class="space-y-3">
        <div id="google-signup-button" class="h-0 overflow-hidden"></div>
        <button
          type="button"
          :disabled="!googleAuth.isInitialized.value || isGoogleLoading"
          class="w-full h-[52px] inline-flex items-center justify-center gap-2.5 rounded-xl bg-surface border border-ink-200 text-ink-900 font-semibold hover:bg-ink-50 disabled:opacity-60 transition-colors"
          @click="googleAuth.signIn()"
        >
          <font-awesome-icon :icon="['fab', 'google']" class="text-lg" />
          Continue with Google
        </button>

        <div class="flex items-center gap-3 py-1">
          <div class="flex-1 border-t border-ink-200"></div>
          <span class="text-[13px] font-semibold text-ink-500">or</span>
          <div class="flex-1 border-t border-ink-200"></div>
        </div>

        <button
          type="button"
          class="w-full h-[52px] inline-flex items-center justify-center rounded-xl border border-outline text-ink-800 font-semibold hover:bg-ink-50 transition-colors"
          @click="startEmail"
        >
          Sign up with email
        </button>
      </div>

      <div class="mt-7 space-y-4 text-center">
        <div class="flex items-center justify-center gap-2">
          <span class="flex gap-0.5">
            <font-awesome-icon v-for="i in 5" :key="i" :icon="['fas', 'star']" class="text-coral text-sm" />
          </span>
          <span class="text-sm text-ink-600">Loved by <span class="font-bold font-mono">4,500+</span> teachers</span>
        </div>
        <p class="text-[12.5px] text-ink-500">
          By continuing you agree to our
          <a :href="`${marketingUrl}/terms`" class="font-bold text-ink-700 hover:opacity-80">Terms</a>
          &amp;
          <a :href="`${marketingUrl}/privacy-policy`" class="font-bold text-ink-700 hover:opacity-80">Privacy Policy</a>.
        </p>
        <p class="text-sm text-ink-600">
          Already have an account?
          <RouterLink to="/login" class="font-semibold text-ink-900 hover:opacity-80">Sign in</RouterLink>
        </p>
      </div>
    </template>

    <!-- EMAIL: the sign-up form -->
    <template v-else>
      <button
        type="button"
        class="flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-700 mb-4 transition-colors"
        @click="mode = 'choose'"
      >
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-xs" /> Back
      </button>

      <div class="text-center mb-6">
        <h1 class="text-2xl font-bold text-ink-900">Create your account</h1>
        <p class="text-ink-500 mt-1">Free to start. No card needed.</p>
      </div>

      <div
        v-if="errorMessage"
        class="mb-5 p-4 bg-danger-soft border border-danger-ink/15 rounded-xl flex items-start gap-3"
      >
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="text-danger-ink mt-0.5" />
        <p class="text-sm text-danger-ink">{{ errorMessage }}</p>
      </div>

      <form class="space-y-4" @submit.prevent="handleRegister">
        <div class="space-y-1.5">
          <label for="name" class="block text-xs font-semibold tracking-wide text-ink-700">YOUR NAME (OPTIONAL)</label>
          <div class="flex items-center gap-2.5 h-12 px-3.5 rounded-xl bg-surface border border-outline focus-within:border-[var(--primary)] focus-within:ring-2 focus-within:ring-[var(--primary)]/20 transition-all">
            <font-awesome-icon :icon="['fas', 'user']" class="text-ink-500 text-sm" />
            <input
              id="name"
              v-model="displayName"
              type="text"
              autocomplete="name"
              placeholder="How should we greet you?"
              class="flex-1 bg-transparent border-0 appearance-none outline-none focus:ring-0 text-ink-900 placeholder:text-ink-400"
            />
          </div>
        </div>

        <div class="space-y-1.5">
          <label for="email" class="block text-xs font-semibold tracking-wide text-ink-700">EMAIL ADDRESS</label>
          <div class="flex items-center gap-2.5 h-12 px-3.5 rounded-xl bg-surface border border-outline focus-within:border-[var(--primary)] focus-within:ring-2 focus-within:ring-[var(--primary)]/20 transition-all">
            <font-awesome-icon :icon="['fas', 'envelope']" class="text-ink-500 text-sm" />
            <input
              id="email"
              v-model="email"
              type="email"
              autocomplete="email"
              required
              placeholder="you@school.com"
              class="flex-1 bg-transparent border-0 appearance-none outline-none focus:ring-0 text-ink-900 placeholder:text-ink-400"
            />
          </div>
        </div>

        <div class="space-y-1.5">
          <label for="password" class="block text-xs font-semibold tracking-wide text-ink-700">PASSWORD</label>
          <div class="flex items-center gap-2.5 h-12 px-3.5 rounded-xl bg-surface border border-outline focus-within:border-[var(--primary)] focus-within:ring-2 focus-within:ring-[var(--primary)]/20 transition-all">
            <font-awesome-icon :icon="['fas', 'lock']" class="text-ink-500 text-sm" />
            <input
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              required
              placeholder="Min. 8 characters"
              class="flex-1 bg-transparent border-0 appearance-none outline-none focus:ring-0 text-ink-900 placeholder:text-ink-400"
            />
            <button type="button" class="text-ink-400 hover:text-ink-600" @click="showPassword = !showPassword">
              <font-awesome-icon :icon="['fas', showPassword ? 'eye-slash' : 'eye']" />
            </button>
          </div>
          <div v-if="passwordStrength" class="flex items-center gap-2 pt-0.5">
            <div class="flex-1 flex gap-1">
              <div
                v-for="i in 4"
                :key="i"
                class="h-1 flex-1 rounded-full"
                :class="i <= passwordStrength.filled ? passwordStrength.bar : 'bg-ink-200'"
              ></div>
            </div>
            <span class="text-[11.5px] font-semibold" :class="passwordStrength.text">{{ passwordStrength.word }}</span>
          </div>
        </div>

        <button
          type="submit"
          :disabled="!isFormValid || isLoading"
          class="w-full h-[52px] rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 disabled:opacity-50 transition-colors"
        >
          {{ isLoading ? 'Creating…' : 'Create account' }}
        </button>
      </form>

      <p class="mt-6 text-center text-sm text-ink-600">
        Already have an account?
        <RouterLink to="/login" class="font-semibold text-ink-900 hover:opacity-80">Sign in</RouterLink>
      </p>
    </template>
  </div>
</template>
